import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import useStudentsTable from '@/hooks/table/useStudentsTable';
import { Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ErrorMessage from '../../error-message/ErrorMessage';
import AlertModal from '../../modals/alert-modal/AlertModal';
import { GenericTable } from '../common/generic-table/GenericTable';
import TableToolBar from './TableToolBar';

const StudentTables: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    table,
    services,
    selectedServiceId,
    attendanceDate,
    isLoading,
    isError,
    isFetching,
    handleExport,
    handleExportAttendance,
    filtering,
    setFiltering,
    setSelectedServiceId,
    isLoadingAllReservations,
    allReservations,
    renderExportButtons,
    openAlertModal,
    setOpenAlertModal
  } = useStudentsTable();

  if (isError) {
    return <ErrorMessage />;
  }

  const selectedService = services?.find(
    (service) => service.id === selectedServiceId
  );

  if (isLoadingAllReservations) {
    return (
      <div className="flex items-center justify-center h-[70vh]">
        <Loader className="animate-spin" />
        <span className="ml-2">Cargando...</span>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2">
        {services && services.length > 0 ? (
          <h1 className="text-2xl font-semibold">
            {`${t('common.viewAttendence')} `}
            <span className="text-primary">{selectedService?.name}</span>
          </h1>
        ) : (
          <h1 className="text-2xl font-semibold">
            {t('common.noServicesAvailable')}
          </h1>
        )}
        <h2 className="text-lg font-medium">{`Fecha: ${attendanceDate}`}</h2>
      </div>
      <div className="grid grid-cols-1 gap-4 my-4 xl:grid-cols-2">
        <Select
          value={selectedServiceId || ''}
          onValueChange={setSelectedServiceId}
        >
          {services && services.length > 0 ? (
            <SelectTrigger className="w-full space-x-2">
              <SelectValue />
            </SelectTrigger>
          ) : null}
          <SelectContent>
            {(services || []).map((service) => (
              <SelectItem
                value={service.id}
                key={service.id}
                disabled={!service.enabled}
              >
                {service.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {renderExportButtons && (
          <div className="flex flex-col items-center w-full gap-2 md:flex-row xl:justify-end">
            <Button
              onClick={handleExport}
              disabled={
                isLoadingAllReservations ||
                !allReservations ||
                allReservations.length === 0
              }
              className="w-full xl:w-auto"
            >
              {t('common.downloadStudentsData')}
            </Button>
            <Button
              onClick={handleExportAttendance}
              disabled={
                isLoadingAllReservations ||
                !allReservations ||
                allReservations.length === 0
              }
              className="w-full xl:w-auto"
            >
              {t('common.downloadAttendanceSheet')}
            </Button>
          </div>
        )}
      </div>
      <div className="space-y-4">
        <TableToolBar
          table={table}
          filtering={filtering}
          setFiltering={setFiltering}
          isFetching={isFetching}
        />
        <GenericTable table={table} isLoading={isLoading} />
      </div>
      <AlertModal
        open={openAlertModal.state}
        onOpenChange={(open) => {
          setOpenAlertModal({ id: openAlertModal.id, state: open });
        }}
        title={t('modals.serviceSold.title')}
        description={t('modals.serviceSold.description')}
        acceptText={t('modals.serviceSold.button')}
        onAccept={() => navigate(`/sales/${openAlertModal.id}`)}
        cancelText={t('modals.alert.close')}
      />
    </>
  );
};

export default StudentTables;
